import { memo, type PropsWithChildren } from "react";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridRowParams } from "@mui/x-data-grid";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useTypedSelector } from "hooks";
import { MenuItem } from "consts/openBanking";
import { selectIsOpenBankingResourceLoading } from "features/openBanking/selectors";
import { TriggerableDialog, TriggerableDialogProps } from "ui/molecules/Dialog";
import ActionPanel from "ui/molecules/ActionPanel";

type Props =
    & Pick<TriggerableDialogProps, 'ModalProps'>
    & GridRowParams
    & {
        readonly menuItem: MenuItem;
        readonly deleteOpenBankingResource: (params: GridRowParams) => Promise<void>;
    };

const DeleteOpenBankingResource = ({
    menuItem,
    children,
    ModalProps,
    deleteOpenBankingResource,
    ...restProps
}: PropsWithChildren<Props>) => {
    const isOpenBankingResourceLoading = useTypedSelector(selectIsOpenBankingResourceLoading(menuItem));

    const onSave = () => deleteOpenBankingResource(restProps);

    return (
        <TriggerableDialog
            TriggerComponent={({ onClick }) => (
                <GridActionsCellItem
                    onClick={onClick}
                    icon={<DeleteForeverIcon />}
                    label='Delete'
                    color='error'
                />
            )}
            DialogActionsComponent={({ onClose }) => (
                <ActionPanel
                    sx={{
                        flexGrow: 1
                    }}
                    isLoading={isOpenBankingResourceLoading}
                    onSave={() => onSave()
                        .then(() => onClose())}
                    onCancel={onClose}
                    SaveActionProps={{
                        children: 'Delete',
                        color: 'error'
                    }}
                />
            )}
            ModalProps={ModalProps}
        >
            <Typography>
                {children}
            </Typography>
        </TriggerableDialog>
    );
};

export default memo(DeleteOpenBankingResource);
